import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The design system's typography scale (`packages/ui/src/styles.css`).
 *
 * tailwind-merge has to be told about these by name. Its default rules decide
 * whether `text-foo` is a SIZE or a COLOUR by pattern — a t-shirt size like
 * `text-sm` is a size, and anything it doesn't recognise falls through to
 * colour. Every name here (`text-body-sm`, `text-label-sm`, …) falls through.
 *
 * The consequence was silent and everywhere: in any `cn()` that combined one of
 * these with a semantic colour — `cn("text-label-sm", done && "text-success-text")`
 * — merge saw two colours, kept the last, and DELETED the size. The element
 * then rendered at the inherited size. On the To Do rail that showed up as
 * labels jumping from 11px to 14px, but nothing about it is specific to that
 * feature: it applies to every call site that styles size and colour together.
 *
 * Keep in step with the `--text-*` tokens in the stylesheet. A name missing
 * here doesn't error — it just quietly goes back to being treated as a colour.
 */
const SFC_FONT_SIZES = [
  "heading-xl",
  "heading-lg",
  "heading-md",
  "heading-sm",
  "body-lg",
  "body-md",
  "body-sm",
  "label-lg",
  "label-md",
  "label-sm",
  "mono-md",
  "mono-sm",
] as const;

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: [...SFC_FONT_SIZES] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
